import React, { useState, useReducer, useEffect, useMemo } from 'react';
import { CheckIcon, ChevronUpDownIcon, CogIcon, DocumentTextIcon, CalculatorIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { Listbox } from '@headlessui/react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import { toast } from 'react-toastify';
import { LoadStep } from '../services/StepLoader';

// Quantity break structure
const quantityBreaks = [
  { min: 1, max: 10, label: 'Prototype (1-10)', setupMultiplier: 1.0, marginMultiplier: 1.5 },
  { min: 11, max: 50, label: 'Small Batch (11-50)', setupMultiplier: 0.8, marginMultiplier: 1.3 },
  { min: 51, max: 200, label: 'Medium Batch (51-200)', setupMultiplier: 0.6, marginMultiplier: 1.2 },
  { min: 201, max: 999999, label: 'Production (201+)', setupMultiplier: 0.4, marginMultiplier: 1.1 }
];

// Material densities (lbs/in³) - constants since not in database
const materialDensities = {
  'Aluminum': 0.0975,
  'Aluminum 6061': 0.0975,
  'Aluminum 7075': 0.101,
  'Steel': 0.283,
  'Steel 1018': 0.283,
  'Steel 4140': 0.283,
  'Stainless Steel': 0.290,
  'Stainless 304': 0.290,
  'Stainless 316': 0.290,
  'Titanium': 0.163,
  'Titanium Ti-6Al-4V': 0.163,
  'Brass': 0.305,
  'Copper': 0.323,
  'Bronze': 0.320,
  'Acetal': 0.054,
  'UHMW': 0.034,
  'Nylon': 0.041,
  'PEEK': 0.047
};

// Quote data reducer for better state management
const quoteReducer = (state, action) => {
  switch (action.type) {
    case 'UPDATE_BASIC_INFO':
      return { ...state, basicInfo: { ...state.basicInfo, ...action.payload } };
    case 'UPDATE_PRODUCTION':
      return { ...state, production: { ...state.production, ...action.payload } };
    case 'UPDATE_POST_PRODUCTION':
      return { ...state, postProduction: { ...state.postProduction, ...action.payload } };
    case 'UPDATE_ADDITIONAL':
      return { ...state, additional: { ...state.additional, ...action.payload } };
    case 'SET_CALCULATED_COSTS':
      return { ...state, calculatedCosts: action.payload };
    default:
      return state;
  }
};

const initialQuoteState = {
  basicInfo: {
    partNumber: '',
    quantity: 10,
    materialId: null,
    materialPricePerPound: 0,
    shopRate: 125,
    stepFile: null,
    volume: 0,
    selectedPart: null,
    manualMaterialCost: 0,
    materialCostMode: 'calculated' // 'calculated' or 'manual'
  },
  production: {
    setupHours: 2,
    programmingHours: 1,
    runTimeMinutes: 45,
    deburringMinutes: 10
  },
  postProduction: {
    inspectionMinutes: 5,
    packagingMinutes: 3
  },
  additional: {
    toolingCost: 0,
    fixturingCost: 0,
    platingCost: 0,
    specialProcessing: []
  },
  calculatedCosts: {
    materialCost: 0,
    setupCost: 0,
    productionCost: 0,
    postProductionCost: 0,
    additionalCost: 0,
    subtotal: 0,
    margin: 0,
    totalPerPiece: 0
  }
};

function RefinedQuoteEngine() {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [quoteData, dispatch] = useReducer(quoteReducer, initialQuoteState);
  const [expandedSections, setExpandedSections] = useState({
    material: true,
    production: true,
    postProduction: false,
    additional: false
  });
  
  // Parts and materials management state
  const [parts, setParts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [partSearchQuery, setPartSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [isPartSelected, setIsPartSelected] = useState(false);

  // Fetch parts and materials on component mount
  useEffect(() => {
    const fetchShopsMaterialsAndParts = async () => {
      if (!user) return;
      setLoading(true);
      try {
        // Fetch materials from Supabase - just name and id, density is in constants
        const { data: materialsData, error: materialsError } = await supabase.from('materials').select('id, name');
        if (materialsError) throw materialsError;
        setMaterials(materialsData || []);
        
        // Set default material if none selected
        if (materialsData && materialsData.length > 0 && !quoteData.basicInfo.materialId) {
          dispatch({
            type: 'UPDATE_BASIC_INFO',
            payload: { materialId: materialsData[0].id }
          });
        }
        
        // Fetch parts from Supabase
        const { data: partsData, error: partsError } = await supabase.from('parts').select('part_number, description').order('part_number');
        if (partsError) throw partsError;
        setParts(partsData || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error('Failed to fetch shops, materials, or parts.');
      } finally {
        setLoading(false);
      }
    };
    fetchShopsMaterialsAndParts();
  }, [user]);

  // Handle STEP file processing - following original pattern
  useEffect(() => {
    if (quoteData.basicInfo.stepFile) {
      LoadStep(quoteData.basicInfo.stepFile).then((model) => {
        if (model.userData && model.userData.volume && model.userData.boxSize) {
          dispatch({
            type: 'UPDATE_BASIC_INFO',
            payload: {
              volume: model.userData.volume / 16387.064, // Convert mm³ to in³
              boxSize: model.userData.boxSize,
              bufferVolume: model.userData.bufferVolume,
              bufferBoxSize: model.userData.bufferBoxSize
            }
          });
        }
      }).catch((err) => {
        console.error('Error loading STEP file:', err);
        toast.error('Failed to load STEP file for volume calculation.');
        dispatch({
          type: 'UPDATE_BASIC_INFO',
          payload: { volume: 0 }
        });
      });
    }
  }, [quoteData.basicInfo.stepFile]);

  // Calculate quote whenever any relevant data changes
  useEffect(() => {
    calculateQuote();
  }, [
    quoteData.basicInfo.quantity,
    quoteData.basicInfo.shopRate,
    quoteData.basicInfo.materialId,
    quoteData.basicInfo.materialPricePerPound,
    quoteData.basicInfo.materialCostMode,
    quoteData.basicInfo.manualMaterialCost,
    quoteData.basicInfo.volume,
    quoteData.basicInfo.bufferVolume,
    quoteData.production,
    quoteData.postProduction,
    quoteData.additional,
    materials
  ]);

  // Handle part search - exact implementation from original
  const handlePartSearch = async () => {
    if (!selectedPart) {
      toast.error('Please select a part to search.');
      return;
    }
  
    try {
      // Search for a STEP file in the part_files table
      const { data: partFilesData, error: partFileError } = await supabase
        .from('part_files')
        .select('file_path')
        .eq('part_number', selectedPart.part_number)
        .or("file_path.ilike.%.step,file_path.ilike.%.stp,file_path.ilike.%.STEP");

      if (partFileError) throw partFileError;
  
      if (partFilesData && partFilesData.length > 0) {
        // Take the first matching STEP file
        const stepFilePath = partFilesData[0].file_path;
  
        // Download the STEP file from Supabase Storage
        const { data: fileData, error: fileError } = await supabase
          .storage
          .from('part-files')
          .download(stepFilePath);
        if (fileError) throw fileError;
  
        // Convert the downloaded file to a File object
        const file = new File([fileData], `${selectedPart.part_number}.step`, { type: 'application/octet-stream' });
        
        dispatch({
          type: 'UPDATE_BASIC_INFO',
          payload: { 
            stepFile: file,
            partNumber: selectedPart.part_number
          }
        });
        
        toast.success('STEP file loaded successfully!');
      } else {
        toast.info('No STEP file found for this part. Please upload a file.');
        dispatch({
          type: 'UPDATE_BASIC_INFO',
          payload: { 
            stepFile: null,
            partNumber: selectedPart.part_number
          }
        });
      }
    } catch (err) {
      console.error('Error searching for part file:', err);
      toast.error('Failed to search for part file.');
      dispatch({
        type: 'UPDATE_BASIC_INFO',
        payload: { 
          stepFile: null,
          partNumber: selectedPart.part_number
        }
      });
    }
  };

  // Filter parts based on search query - from original
  const filteredParts = parts.filter((part) =>
    part.part_number.toLowerCase().includes(partSearchQuery.toLowerCase()) ||
    (part.description && part.description.toLowerCase().includes(partSearchQuery.toLowerCase()))
  );

  const calculateQuote = () => {
    const { basicInfo, production, postProduction, additional } = quoteData;
    const { quantity, materialId, shopRate, volume, bufferVolume, materialCostMode, manualMaterialCost, materialPricePerPound } = basicInfo;
    
    // Find current quantity break
    const currentBreak = quantityBreaks.find(qb => quantity >= qb.min && quantity <= qb.max);
    const material = materials.find(m => m.id === materialId);
    
    // Material cost calculation - handle both modes
    let materialCostPerPiece = 0;
    if (materialCostMode === 'calculated' && (bufferVolume || volume) && material && materialPricePerPound > 0) {
      // Use bufferVolume for material calculation if available, fallback to volume
      const volumeToUse = bufferVolume ? mm3ToInches(bufferVolume) : volume;
      // Get density from constants using material name
      const density = materialDensities[material.name] || 0;
      // Calculate weight: Volume (in³) × Density (lbs/in³)
      const weightInPounds = volumeToUse * density;
      // Calculate cost: Weight × Price per pound
      materialCostPerPiece = weightInPounds * materialPricePerPound;
    } else if (materialCostMode === 'manual') {
      materialCostPerPiece = manualMaterialCost;
    }
    // If no volume data, no material selected, or no price entered, material cost remains 0
    
    // Setup costs (distributed across quantity with break adjustments)
    const baseSetupCost = (production.setupHours + production.programmingHours) * shopRate;
    const adjustedSetupCost = baseSetupCost * currentBreak.setupMultiplier;
    const setupCostPerPiece = adjustedSetupCost / quantity;
    
    // Production costs (per piece)
    const productionTimeHours = (production.runTimeMinutes + production.deburringMinutes) / 60;
    const productionCostPerPiece = productionTimeHours * shopRate;
    
    // Post-production costs (per piece)
    const postProductionTimeHours = (postProduction.inspectionMinutes + postProduction.packagingMinutes) / 60;
    const postProductionCostPerPiece = postProductionTimeHours * shopRate;
    
    // Additional costs (distributed across quantity)
    const additionalCostPerPiece = (additional.toolingCost + additional.fixturingCost + additional.platingCost) / quantity;
    
    // Subtotal before margin
    const subtotalPerPiece = materialCostPerPiece + setupCostPerPiece + productionCostPerPiece + postProductionCostPerPiece + additionalCostPerPiece;
    
    // Apply margin based on quantity break
    const marginAmount = subtotalPerPiece * (currentBreak.marginMultiplier - 1);
    const totalPerPiece = subtotalPerPiece + marginAmount;
    
    dispatch({
      type: 'SET_CALCULATED_COSTS',
      payload: {
        materialCost: materialCostPerPiece,
        setupCost: setupCostPerPiece,
        productionCost: productionCostPerPiece,
        postProductionCost: postProductionCostPerPiece,
        additionalCost: additionalCostPerPiece,
        subtotal: subtotalPerPiece,
        margin: marginAmount,
        totalPerPiece: totalPerPiece,
        quantityBreak: currentBreak
      }
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const generateQuantityBreakTable = () => {
    const testQuantities = [1, 5, 10, 25, 50, 100, 250, 500];
    return testQuantities.map(qty => {
      const currentBreak = quantityBreaks.find(qb => qty >= qb.min && qty <= qb.max);
      const material = materials.find(m => m.id === quoteData.basicInfo.materialId);
      
      // Material cost calculation - same logic as main calculation
      let materialCostPerPiece = 0;
      if (quoteData.basicInfo.materialCostMode === 'calculated' && (quoteData.basicInfo.bufferVolume || quoteData.basicInfo.volume) && material && quoteData.basicInfo.materialPricePerPound > 0) {
        const volumeToUse = quoteData.basicInfo.bufferVolume ? mm3ToInches(quoteData.basicInfo.bufferVolume) : quoteData.basicInfo.volume;
        // Get density from constants using material name
        const density = materialDensities[material.name] || 0;
        // Calculate weight: Volume (in³) × Density (lbs/in³)
        const weightInPounds = volumeToUse * density;
        // Calculate cost: Weight × Price per pound
        materialCostPerPiece = weightInPounds * quoteData.basicInfo.materialPricePerPound;
      } else if (quoteData.basicInfo.materialCostMode === 'manual') {
        materialCostPerPiece = quoteData.basicInfo.manualMaterialCost;
      }
      
      const baseSetupCost = (quoteData.production.setupHours + quoteData.production.programmingHours) * quoteData.basicInfo.shopRate;
      const adjustedSetupCost = baseSetupCost * currentBreak.setupMultiplier;
      const setupCostPerPiece = adjustedSetupCost / qty;
      const productionTimeHours = (quoteData.production.runTimeMinutes + quoteData.production.deburringMinutes) / 60;
      const productionCostPerPiece = productionTimeHours * quoteData.basicInfo.shopRate;
      const postProductionTimeHours = (quoteData.postProduction.inspectionMinutes + quoteData.postProduction.packagingMinutes) / 60;
      const postProductionCostPerPiece = postProductionTimeHours * quoteData.basicInfo.shopRate;
      const additionalCostPerPiece = (quoteData.additional.toolingCost + quoteData.additional.fixturingCost + quoteData.additional.platingCost) / qty;
      const subtotalPerPiece = materialCostPerPiece + setupCostPerPiece + productionCostPerPiece + postProductionCostPerPiece + additionalCostPerPiece;
      const totalPerPiece = subtotalPerPiece * currentBreak.marginMultiplier;
      
      return {
        quantity: qty,
        pricePerPiece: totalPerPiece,
        totalPrice: totalPerPiece * qty,
        break: currentBreak.label
      };
    });
  };

  const steps = [
    { number: 1, name: 'Basic Info', icon: DocumentTextIcon },
    { number: 2, name: 'Production', icon: CogIcon },
    { number: 3, name: 'Pricing', icon: CalculatorIcon }
  ];

  // Show loading state while fetching parts
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading parts database...</p>
        </div>
      </div>
    );
  }

  // Utility functions from original
  const mm3ToInches = (v) => v / 16387.064;
  const mm3ToCm = (v) => v / 1000;
  const mmToInches = (v) => v / 25.4;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Quote Engine</h1>
          <p className="text-gray-600 mt-2">Create accurate quotes for machined parts</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <nav aria-label="Progress">
            <ol className="flex items-center justify-center space-x-8">
              {steps.map((step) => (
                <li key={step.number} className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                      currentStep >= step.number
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : 'border-gray-300 text-gray-500'
                    }`}
                  >
                    <step.icon className="w-5 h-5" />
                  </div>
                  <span className={`ml-3 text-sm font-medium ${
                    currentStep >= step.number ? 'text-indigo-600' : 'text-gray-500'
                  }`}>
                    {step.name}
                  </span>
                </li>
              ))}
            </ol>
          </nav>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {currentStep === 1 && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-semibold mb-6">Basic Information</h2>
                
                {/* Part Source Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Part Source <span className="text-red-500">*</span>
                  </label>
                  <div className="flex space-x-6">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="existing"
                        checked={isPartSelected}
                        onChange={() => setIsPartSelected(true)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Select Existing Part</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="upload"
                        checked={!isPartSelected}
                        onChange={() => setIsPartSelected(false)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Upload New File</span>
                    </label>
                  </div>
                </div>

                {/* Existing Part Selection - Using real Listbox component */}
                {isPartSelected && (
                  <div className="mb-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Part <span className="text-red-500">*</span>
                      </label>
                                              <Listbox value={selectedPart} onChange={(part) => {
                          setSelectedPart(part);
                          dispatch({
                            type: 'UPDATE_BASIC_INFO',
                            payload: { selectedPart: part }
                          });
                        }}>
                        <div className="relative">
                          <Listbox.Button className="relative w-full cursor-default rounded-lg border border-gray-300 bg-white py-2.5 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm">
                            <span className="block truncate">
                              {selectedPart ? `${selectedPart.part_number} - ${selectedPart.description}` : 'Select a part'}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                            </span>
                          </Listbox.Button>
                          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <div className="p-2">
                              <input
                                type="text"
                                value={partSearchQuery}
                                onChange={(e) => setPartSearchQuery(e.target.value)}
                                placeholder="Search parts..."
                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                            {filteredParts.map((part) => (
                              <Listbox.Option
                                key={part.part_number}
                                value={part}
                                className={({ active }) =>
                                  `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-indigo-100 text-indigo-900' : 'text-gray-900'
                                  }`
                                }
                              >
                                {({ selected }) => (
                                  <>
                                    <span
                                      className={`block truncate ${
                                        selected ? 'font-medium' : 'font-normal'
                                      }`}
                                    >
                                      {part.part_number} - {part.description}
                                    </span>
                                    {selected ? (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </div>
                      </Listbox>
                    </div>
                    
                    <div className="flex justify-end">
                      <button
                        onClick={handlePartSearch}
                        disabled={!selectedPart}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200 shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                      >
                        <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                        Search
                      </button>
                    </div>
                  </div>
                )}

                {/* File Upload Section */}
                {!isPartSelected && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      STEP File Upload
                    </label>
                    <div 
                      className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-500 transition duration-200 cursor-pointer bg-gray-50"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files[0];
                        if (
                          file &&
                          (file.name.endsWith('.step') ||
                            file.name.endsWith('.stp') ||
                            file.name.endsWith('.STEP'))
                        ) {
                          dispatch({
                            type: 'UPDATE_BASIC_INFO',
                            payload: { stepFile: file }
                          });
                        } else {
                          toast.error('Please upload a valid STEP file (.step, .stp, .STEP).');
                        }
                      }}
                      onClick={() => document.getElementById('stepFileInput').click()}
                    >
                      <input
                        id="stepFileInput"
                        type="file"
                        accept=".step,.stp,.STEP"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (
                            file &&
                            (file.name.endsWith('.step') ||
                              file.name.endsWith('.stp') ||
                              file.name.endsWith('.STEP'))
                          ) {
                            dispatch({
                              type: 'UPDATE_BASIC_INFO',
                              payload: { stepFile: file }
                            });
                          } else {
                            toast.error('Please upload a valid STEP file (.step, .stp, .STEP).');
                          }
                        }}
                        className="hidden"
                      />
                      <div className="flex justify-center items-center space-x-2">
                        <svg
                          className="w-6 h-6 text-blue-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7m-9-4h.01M12 3v9m-3-3l3-3 3 3"
                          ></path>
                        </svg>
                        <p className="text-gray-600 font-medium">
                          Browse Files or drag and drop <span className="text-blue-500">.STEP / .STP</span> up to 25MB
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Your files are safe, secure and retain all intellectual rights.
                      </p>
                      {quoteData.basicInfo.stepFile && (
                        <p className="mt-2 text-sm text-indigo-600">
                          Selected: {quoteData.basicInfo.stepFile.name}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Part Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={quoteData.basicInfo.partNumber}
                      onChange={(e) => dispatch({
                        type: 'UPDATE_BASIC_INFO',
                        payload: { partNumber: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g., PART-001"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantity <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={quoteData.basicInfo.quantity}
                      onChange={(e) => dispatch({
                        type: 'UPDATE_BASIC_INFO',
                        payload: { quantity: parseInt(e.target.value) || 1 }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      min="1"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Material <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={quoteData.basicInfo.materialId || ''}
                      onChange={(e) => dispatch({
                        type: 'UPDATE_BASIC_INFO',
                        payload: { materialId: parseInt(e.target.value) }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Select Material</option>
                      {materials.map(material => (
                        <option key={material.id} value={material.id}>
                          {material.name} (ρ={materialDensities[material.name] || 0} lbs/in³)
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {quoteData.basicInfo.materialCostMode === 'calculated' && quoteData.basicInfo.materialId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Material Price ($/lb) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={quoteData.basicInfo.materialPricePerPound}
                        onChange={(e) => dispatch({
                          type: 'UPDATE_BASIC_INFO',
                          payload: { materialPricePerPound: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        min="0"
                        step="0.01"
                        placeholder="e.g., 3.50"
                      />
                      <p className="text-xs text-gray-500 mt-1">Current market price per pound for this material</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Material Cost Mode <span className="text-red-500">*</span>
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="calculated"
                          checked={quoteData.basicInfo.materialCostMode === 'calculated'}
                          onChange={(e) => dispatch({
                            type: 'UPDATE_BASIC_INFO',
                            payload: { materialCostMode: e.target.value }
                          })}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Calculated</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="manual"
                          checked={quoteData.basicInfo.materialCostMode === 'manual'}
                          onChange={(e) => dispatch({
                            type: 'UPDATE_BASIC_INFO',
                            payload: { materialCostMode: e.target.value }
                          })}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Manual</span>
                      </label>
                    </div>
                  </div>
                  
                  {quoteData.basicInfo.materialCostMode === 'manual' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Manual Material Cost ($/part) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={quoteData.basicInfo.manualMaterialCost}
                        onChange={(e) => dispatch({
                          type: 'UPDATE_BASIC_INFO',
                          payload: { manualMaterialCost: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        min="0"
                        step="0.01"
                        placeholder="e.g., 5.50"
                      />
                      <p className="text-xs text-gray-500 mt-1">Total material cost per part</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shop Rate ($/hr) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={quoteData.basicInfo.shopRate}
                      onChange={(e) => dispatch({
                        type: 'UPDATE_BASIC_INFO',
                        payload: { shopRate: parseFloat(e.target.value) || 0 }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      min="0"
                      step="5"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Part Volume (in³)
                    </label>
                    <input
                      type="number"
                      value={quoteData.basicInfo.volume}
                      onChange={(e) => dispatch({
                        type: 'UPDATE_BASIC_INFO',
                        payload: { volume: parseFloat(e.target.value) || 0 }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      min="0"
                      step="0.001"
                      placeholder="Enter manually or upload STEP file"
                      disabled={quoteData.basicInfo.materialCostMode === 'manual'}
                    />
                    {quoteData.basicInfo.materialCostMode === 'manual' && (
                      <p className="text-xs text-gray-500 mt-1">Volume not needed when using manual material cost</p>
                    )}
                  </div>
                </div>

                {/* Display STEP file info if loaded - following original pattern */}
                {quoteData.basicInfo.stepFile && (
                  <div className="mt-6">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center mb-3">
                        <CheckIcon className="h-5 w-5 text-green-600 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-green-800">
                            STEP File Loaded: {quoteData.basicInfo.stepFile.name}
                          </p>
                        </div>
                      </div>
                      
                      {/* Volume and size info like original */}
                      <div className="grid grid-cols-2 gap-4 text-xs text-green-700">
                        <div>
                          <span className="font-medium">Part Volume:</span>
                          <div>{mm3ToInches(quoteData.basicInfo.volume * 16387.064).toFixed(2)} in³</div>
                          <div className="text-green-600">({mm3ToCm(quoteData.basicInfo.volume * 16387.064).toFixed(2)} cm³)</div>
                        </div>
                        {quoteData.basicInfo.boxSize && (
                          <div>
                            <span className="font-medium">Dimensions:</span>
                            <div>
                              {mmToInches(quoteData.basicInfo.boxSize.x).toFixed(3)} × {' '}
                              {mmToInches(quoteData.basicInfo.boxSize.y).toFixed(3)} × {' '}
                              {mmToInches(quoteData.basicInfo.boxSize.z).toFixed(3)} in
                            </div>
                          </div>
                        )}
                        {quoteData.basicInfo.bufferVolume && (
                          <div>
                            <span className="font-medium">Stock Volume (+1/8"):</span>
                            <div>{mm3ToInches(quoteData.basicInfo.bufferVolume).toFixed(2)} in³</div>
                            <div className="text-green-600">({mm3ToCm(quoteData.basicInfo.bufferVolume).toFixed(2)} cm³)</div>
                          </div>
                        )}
                        {quoteData.basicInfo.bufferBoxSize && (
                          <div>
                            <span className="font-medium">Stock Dimensions:</span>
                            <div>
                              {mmToInches(quoteData.basicInfo.bufferBoxSize.x).toFixed(3)} × {' '}
                              {mmToInches(quoteData.basicInfo.bufferBoxSize.y).toFixed(3)} × {' '}
                              {mmToInches(quoteData.basicInfo.bufferBoxSize.z).toFixed(3)} in
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Info note about material cost calculation */}
                {!quoteData.basicInfo.stepFile && quoteData.basicInfo.materialCostMode === 'calculated' && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <span className="font-medium">Note:</span> Upload a STEP file to automatically calculate part weight and material costs, 
                      or switch to "Manual" mode to enter material costs directly.
                    </p>
                  </div>
                )}

                {/* Show calculated weight info when available */}
                {quoteData.basicInfo.stepFile && quoteData.basicInfo.materialId && quoteData.basicInfo.materialPricePerPound > 0 && (
                  <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                    {(() => {
                      const material = materials.find(m => m.id === quoteData.basicInfo.materialId);
                      const density = materialDensities[material?.name] || 0;
                      const volumeToUse = quoteData.basicInfo.bufferVolume ? mm3ToInches(quoteData.basicInfo.bufferVolume) : quoteData.basicInfo.volume;
                      const weightInPounds = volumeToUse * density;
                      const materialCost = weightInPounds * quoteData.basicInfo.materialPricePerPound;
                      
                      return (
                        <div className="text-sm text-indigo-800">
                          <span className="font-medium">Material Calculation:</span>
                          <div className="mt-1 text-xs space-y-1">
                            <div>Volume: {volumeToUse.toFixed(3)} in³</div>
                            <div>Weight: {weightInPounds.toFixed(3)} lbs (@ {density} lbs/in³)</div>
                            <div>Cost: ${materialCost.toFixed(2)} (@ ${quoteData.basicInfo.materialPricePerPound}/lb)</div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6">
                {/* Production Setup */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h3 className="text-lg font-semibold mb-4">Setup & Programming</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Setup Time (hours)
                      </label>
                      <input
                        type="number"
                        value={quoteData.production.setupHours}
                        onChange={(e) => dispatch({
                          type: 'UPDATE_PRODUCTION',
                          payload: { setupHours: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        min="0"
                        step="0.25"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Programming Time (hours)
                      </label>
                      <input
                        type="number"
                        value={quoteData.production.programmingHours}
                        onChange={(e) => dispatch({
                          type: 'UPDATE_PRODUCTION',
                          payload: { programmingHours: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        min="0"
                        step="0.25"
                      />
                    </div>
                  </div>
                </div>

                {/* Production Times */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h3 className="text-lg font-semibold mb-4">Production Times (per piece)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Machine Time (minutes)
                      </label>
                      <input
                        type="number"
                        value={quoteData.production.runTimeMinutes}
                        onChange={(e) => dispatch({
                          type: 'UPDATE_PRODUCTION',
                          payload: { runTimeMinutes: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        min="0"
                        step="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Deburring Time (minutes)
                      </label>
                      <input
                        type="number"
                        value={quoteData.production.deburringMinutes}
                        onChange={(e) => dispatch({
                          type: 'UPDATE_PRODUCTION',
                          payload: { deburringMinutes: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        min="0"
                        step="1"
                      />
                    </div>
                  </div>
                </div>

                {/* Post Production */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h3 className="text-lg font-semibold mb-4">Post Production (per piece)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Inspection Time (minutes)
                      </label>
                      <input
                        type="number"
                        value={quoteData.postProduction.inspectionMinutes}
                        onChange={(e) => dispatch({
                          type: 'UPDATE_POST_PRODUCTION',
                          payload: { inspectionMinutes: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        min="0"
                        step="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Packaging Time (minutes)
                      </label>
                      <input
                        type="number"
                        value={quoteData.postProduction.packagingMinutes}
                        onChange={(e) => dispatch({
                          type: 'UPDATE_POST_PRODUCTION',
                          payload: { packagingMinutes: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        min="0"
                        step="1"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Costs */}
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h3 className="text-lg font-semibold mb-4">Additional Costs (one-time)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tooling Cost ($)
                      </label>
                      <input
                        type="number"
                        value={quoteData.additional.toolingCost}
                        onChange={(e) => dispatch({
                          type: 'UPDATE_ADDITIONAL',
                          payload: { toolingCost: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        min="0"
                        step="10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fixturing Cost ($)
                      </label>
                      <input
                        type="number"
                        value={quoteData.additional.fixturingCost}
                        onChange={(e) => dispatch({
                          type: 'UPDATE_ADDITIONAL',
                          payload: { fixturingCost: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        min="0"
                        step="10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Plating Cost ($)
                      </label>
                      <input
                        type="number"
                        value={quoteData.additional.platingCost}
                        onChange={(e) => dispatch({
                          type: 'UPDATE_ADDITIONAL',
                          payload: { platingCost: parseFloat(e.target.value) || 0 }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        min="0"
                        step="10"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-semibold mb-6">Quantity Break Pricing</h2>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Price Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Price per Piece
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Price
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {generateQuantityBreakTable().map((row, index) => (
                        <tr key={index} className={row.quantity === quoteData.basicInfo.quantity ? 'bg-indigo-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {row.quantity}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {row.break}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ${row.pricePerPiece.toFixed(2)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ${row.totalPrice.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 p-4 bg-indigo-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-indigo-900 mb-2">Current Quote Summary</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-indigo-700">Part Number:</span>
                      <span className="ml-2 font-medium">{quoteData.basicInfo.partNumber || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-indigo-700">Quantity:</span>
                      <span className="ml-2 font-medium">{quoteData.basicInfo.quantity}</span>
                    </div>
                    <div>
                      <span className="text-indigo-700">Category:</span>
                      <span className="ml-2 font-medium">{quoteData.calculatedCosts.quantityBreak?.label}</span>
                    </div>
                    <div>
                      <span className="text-indigo-700">Price per Piece:</span>
                      <span className="ml-2 font-medium text-lg">${quoteData.calculatedCosts.totalPerPiece?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8">
              <button
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                disabled={currentStep === 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {currentStep < 3 ? (
                <button
                  onClick={() => setCurrentStep(Math.min(3, currentStep + 1))}
                  disabled={
                    currentStep === 1 && (
                      !quoteData.basicInfo.partNumber ||
                      !quoteData.basicInfo.quantity ||
                      !quoteData.basicInfo.shopRate ||
                      !quoteData.basicInfo.materialId ||
                      (quoteData.basicInfo.materialCostMode === 'manual' && quoteData.basicInfo.manualMaterialCost <= 0) ||
                      (quoteData.basicInfo.materialCostMode === 'calculated' && quoteData.basicInfo.materialPricePerPound <= 0)
                    )
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={() => {
                    // Generate PDF or save quote
                    alert('Quote generated! (PDF generation would happen here)');
                  }}
                  className="px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700"
                >
                  Generate Quote
                </button>
              )}
            </div>
          </div>

          {/* Sidebar - Cost Breakdown */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold mb-4">Cost Breakdown</h3>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-gray-600">
                      Material
                      {quoteData.basicInfo.materialCostMode === 'calculated' && (
                        !quoteData.basicInfo.stepFile && !quoteData.basicInfo.volume ? (
                          <span className="text-xs text-gray-400 block">No volume data</span>
                        ) : !quoteData.basicInfo.materialPricePerPound ? (
                          <span className="text-xs text-gray-400 block">No price/lb entered</span>
                        ) : (
                          (() => {
                            const material = materials.find(m => m.id === quoteData.basicInfo.materialId);
                            const density = materialDensities[material?.name] || 0;
                            const volumeToUse = quoteData.basicInfo.bufferVolume ? mm3ToInches(quoteData.basicInfo.bufferVolume) : quoteData.basicInfo.volume;
                            const weightInPounds = volumeToUse * density;
                            return <span className="text-xs text-gray-400 block">{weightInPounds.toFixed(3)} lbs @ ${quoteData.basicInfo.materialPricePerPound}/lb</span>;
                          })()
                        )
                      )}
                    </span>
                    <span className="text-sm font-medium">${quoteData.calculatedCosts.materialCost?.toFixed(2) || '0.00'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-gray-600">Setup (per piece)</span>
                    <span className="text-sm font-medium">${quoteData.calculatedCosts.setupCost?.toFixed(2) || '0.00'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-gray-600">Production</span>
                    <span className="text-sm font-medium">${quoteData.calculatedCosts.productionCost?.toFixed(2) || '0.00'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-gray-600">Post Production</span>
                    <span className="text-sm font-medium">${quoteData.calculatedCosts.postProductionCost?.toFixed(2) || '0.00'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-gray-600">Additional</span>
                    <span className="text-sm font-medium">${quoteData.calculatedCosts.additionalCost?.toFixed(2) || '0.00'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-gray-600">Subtotal</span>
                    <span className="text-sm font-medium">${quoteData.calculatedCosts.subtotal?.toFixed(2) || '0.00'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-sm text-gray-600">
                      Margin
                      {quoteData.calculatedCosts.quantityBreak && (
                        <span className="text-xs text-gray-400 block">
                          {((quoteData.calculatedCosts.quantityBreak.marginMultiplier - 1) * 100).toFixed(0)}%
                        </span>
                      )}
                    </span>
                    <span className="text-sm font-medium">${quoteData.calculatedCosts.margin?.toFixed(2) || '0.00'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-3 bg-indigo-50 rounded-lg px-3 mt-4">
                    <span className="text-base font-semibold text-indigo-900">Total per Piece</span>
                    <span className="text-lg font-bold text-indigo-900">${quoteData.calculatedCosts.totalPerPiece?.toFixed(2) || '0.00'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 text-sm text-gray-600">
                    <span>Total Order Value</span>
                    <span className="font-medium">${((quoteData.calculatedCosts.totalPerPiece || 0) * quoteData.basicInfo.quantity).toFixed(2)}</span>
                  </div>
                </div>

                {quoteData.calculatedCosts.quantityBreak && (
                  <div className="mt-6 p-3 bg-yellow-50 rounded-lg">
                    <div className="text-xs font-medium text-yellow-800 mb-1">Current Pricing Tier</div>
                    <div className="text-sm text-yellow-700">{quoteData.calculatedCosts.quantityBreak.label}</div>
                  </div>
                )}

                {/* Note when material cost is zero */}
                {quoteData.calculatedCosts.materialCost === 0 && quoteData.basicInfo.materialCostMode === 'calculated' && (
                  <div className="mt-4 p-3 bg-orange-50 rounded-lg">
                    <div className="text-xs font-medium text-orange-800 mb-1">Material Cost Missing</div>
                    <div className="text-xs text-orange-700">
                      {!quoteData.basicInfo.stepFile && !quoteData.basicInfo.volume 
                        ? "Upload a STEP file or enter volume manually, then set price per pound."
                        : !quoteData.basicInfo.materialPricePerPound
                        ? "Enter the material price per pound to calculate material costs."
                        : "Select a material and enter price per pound to include material costs."
                      }
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RefinedQuoteEngine;